/**
 * Die Grenzen, innerhalb derer Vespera Martin anrufen darf. (Ticket 2728)
 *
 * Martin, 01.10.2026: Ruhezeit 22 bis 5 Uhr (Wiener Zeit), höchstens 5 Anrufe
 * am Tag, höchstens 15 Minuten je Anruf. Server (Sperrliste, server/
 * anruf-ausloesen.ts) und Einstellungsseite lesen DIESELBEN Zahlen — der Text,
 * den Martin sieht, kann so nicht von der Sperre abweichen.
 *
 * 02.10.2026: Ruhezeit und Tageslimit sind Kontowerte (Einstellungen →
 * Mitteilungen → „Anrufe von Vespera“): `anrufRuheAb`, `anrufRuheBis`,
 * `anrufTageslimit`. Die Zahlen hier sind nur noch die VORGABE, wenn nichts
 * (oder nichts Gültiges) gespeichert ist. `anrufRuheAb === anrufRuheBis`
 * heißt „keine Ruhezeit“. Die Höchstdauer bleibt fest.
 */
export const RUHEZEIT_AB = 22;
export const RUHEZEIT_BIS = 5;
export const TAGESLIMIT = 5;
export const HOECHSTDAUER_MINUTEN = 15;
/** Grenzen der Einstellung „Höchstens … Anrufe pro Tag“. */
export const TAGESLIMIT_MIN = 1;
export const TAGESLIMIT_MAX = 20;
/** Der Wert 0 heißt „unbegrenzt“ (nur wer es selbst wählt; ohne Wahl gilt die Vorgabe 5). */
export const TAGESLIMIT_UNBEGRENZT = 0;

export interface AnrufGrenzen {
  /** Ruhezeit ab (Stunde 0–23, Wiener Zeit). */
  ruheAb: number;
  /** Ruhezeit bis (Stunde 0–23, ausschließlich). Gleich `ruheAb` = keine Ruhezeit. */
  ruheBis: number;
  /** Höchstens so viele Anrufe je Wiener Kalendertag; 0 = unbegrenzt. */
  tageslimit: number;
}

export const ANRUF_GRENZEN_VORGABE: Readonly<AnrufGrenzen> = Object.freeze({
  ruheAb: RUHEZEIT_AB,
  ruheBis: RUHEZEIT_BIS,
  tageslimit: TAGESLIMIT,
});

/** Eine gültige Stunde (Ganzzahl 0–23) oder undefined. */
export function anrufStunde(w: unknown): number | undefined {
  return typeof w === "number" && Number.isInteger(w) && w >= 0 && w <= 23 ? w : undefined;
}

/** Ein gültiges Tageslimit (Ganzzahl 1–20, oder 0 = unbegrenzt) oder undefined. */
export function anrufTageslimit(w: unknown): number | undefined {
  return typeof w === "number" && Number.isInteger(w) &&
    (w === TAGESLIMIT_UNBEGRENZT || (w >= TAGESLIMIT_MIN && w <= TAGESLIMIT_MAX))
    ? w
    : undefined;
}

/**
 * Die geltenden Grenzen aus den (rohen) Einstellungen — jedes Feld einzeln:
 * fehlt es oder ist es ungültig, gilt die Vorgabe (22–5, 5 am Tag).
 */
export function anrufGrenzenVon(s: unknown): AnrufGrenzen {
  const r = (s && typeof s === "object" ? s : {}) as Record<string, unknown>;
  return {
    ruheAb: anrufStunde(r.anrufRuheAb) ?? RUHEZEIT_AB,
    ruheBis: anrufStunde(r.anrufRuheBis) ?? RUHEZEIT_BIS,
    tageslimit: anrufTageslimit(r.anrufTageslimit) ?? TAGESLIMIT,
  };
}

/** „22–5 Uhr kein Anruf“ bzw. „keine Ruhezeit“. */
function ruhezeitText(g: AnrufGrenzen): string {
  return g.ruheAb === g.ruheBis ? "keine Ruhezeit" : `${g.ruheAb}–${g.ruheBis} Uhr kein Anruf`;
}

/** „höchstens 5 Anrufe am Tag“ bzw. „unbegrenzt Anrufe am Tag“. */
export function tageslimitText(n: number): string {
  if (n === TAGESLIMIT_UNBEGRENZT) return "Anrufe am Tag unbegrenzt";
  return `höchstens ${n} ${n === 1 ? "Anruf" : "Anrufe"} am Tag`;
}

/** Die Zeile unter dem Schalter „Vespera darf mich anrufen“. */
export function anrufGrenzenText(g: AnrufGrenzen = ANRUF_GRENZEN_VORGABE): string {
  return (
    `${ruhezeitText(g)} · ${tageslimitText(g.tageslimit)} · ` +
    `jeder höchstens ${HOECHSTDAUER_MINUTEN} Minuten`
  );
}
